import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '~/eds/src/components/Button/Button';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { SharepointStatusType } from '~/enums/SharepointType';
import {
  useAdminConsent,
  useSharepointConfiguration,
} from '~/features/integrations/hooks';
import { api } from '~/redux/api/createApi';
import { SPEConfiguration } from '~/redux/api/methods/spe';
import Card from '~/ui/components/Card/index';

import IntegrationDetailCard from '../IntegrationDetailCard';
import MicrosoftWordIntegrationCard from './MicrosoftWordIntegrationCard';

function DocumentEditingDetailCard({
  logoSrc,
  rightActionText,
  title,
}: {
  logoSrc: string;
  rightActionText: string;
  title: string;
}) {
  const intl = useIntl();

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isConfigureModalVisible, setIsConfigureModalVisible] = useState(false);
  const [
    isDeleteConfigurationModalVisible,
    setIsDeleteConfigurationModalVisible,
  ] = useState(false);

  const [integrationConfiguration, setIntegrationConfiguration] = useState({
    tenantId: '',
    sharepointUrl: '',
    status: SharepointStatusType.NotConnected,
  } as SPEConfiguration);
  const { integrationConfigurationStatus, saveConfiguration, isLoading } =
    useSharepointConfiguration();

  const { isConsentLoading, handleGrantAdminConsent } = useAdminConsent({
    onSuccess: (tenantId) => {
      setIsConfigureModalVisible(true);
      setIntegrationConfiguration((prev) => ({
        ...prev,
        tenantId,
      }));
    },
  });
  const [deleteSharepointConfiguration, deleteResult] =
    api.endpoints.deleteSharepointConfiguration.useMutation();
  const { isLoading: isDeleting } = deleteResult;

  function handleClose() {
    setIsCollapsed(true);
  }
  function handleConfigure() {
    setIsCollapsed(false);
  }
  const handleHideDeleteModal = () => {
    setIsDeleteConfigurationModalVisible(false);
  };
  const handleHideConfigureModal = () => {
    setIsConfigureModalVisible(false);
  };

  const getStatusInfo = () => {
    switch (integrationConfigurationStatus?.status) {
      case SharepointStatusType.AuthenticationError:
        return intl.formatMessage({
          id: 'DocumentEditingDetailCard.authenticationErrorInfo',
          defaultMessage:
            'Check your authentication login details and try again.',
          description: 'Authentication Error info text',
        });
      case SharepointStatusType.AuthorizedValidationError:
        return intl.formatMessage({
          id: 'DocumentEditingDetailCard.authorizedValidationErrorInfo',
          defaultMessage:
            'The SharePoint Site URL and Microsoft Tenant ID are invalid.\nCheck your configuration information and try again.',
          description: 'Authentication Error info text',
        });
      default:
        return '';
    }
  };

  const onClickDelete = async () => {
    return deleteSharepointConfiguration({
      rtkQueryOptions: {
        showSuccessToast: intl.formatMessage({
          id: 'DocumentEditingDetailCard.deleteConfigurationSuccess',
          defaultMessage: 'Microsoft Word integration has been deleted.',
          description:
            'Success message when Microsoft Word integration is deleted.',
        }),
        showErrorToast: intl.formatMessage({
          id: 'DocumentEditingDetailCard.deleteConfigurationError',
          defaultMessage:
            'Unable to delete Microsoft Word integration, please try again.',
          description:
            'Error message when failing to delete Microsoft Word integration',
        }),
      },
    });
  };

  const footer = (
    <Card.Footer
      actionButtons={[
        <Button
          text={intl.formatMessage({
            id: 'DocumentEditingDetailCard.close',
            defaultMessage: 'Close',
            description:
              'Close button text for Document Editing Integration Card',
          })}
          variant="tertiary"
          onClick={handleClose}
        />,
      ]}
    />
  );

  const deleteModal = (
    <Modal
      enableHideOnPrimaryAction={true}
      isVisible={isDeleteConfigurationModalVisible}
      primaryAction={{
        variant: 'danger',
        text: intl.formatMessage({
          id: 'DocumentEditingDetailCard.deleteButton',
          defaultMessage: 'Delete',
          description: 'Delete button text in confirmation modal',
        }),
        onClick: onClickDelete,
      }}
      title={intl.formatMessage({
        id: 'DocumentEditingDetailCard.deleteConfigurationTitle',
        defaultMessage: 'Delete Microsoft Word Integration',
        description: 'Title for delete configuration modal',
      })}
      children={
        <Layout direction="column" spacing={4}>
          <Text preset="description">
            {intl.formatMessage({
              id: 'DocumentEditingDetailCard.deleteConfigurationDescription1',
              defaultMessage:
                'Removing this integration will disconnect Microsoft Word for all users in your organization, and all authenticated users will lose access immediately. Any documents currently open or partially edited will not be saved, and the process will need to be restarted.',
              description:
                'First paragraph of description for delete configuration modal',
            })}
          </Text>
          <Text preset="description">
            {intl.formatMessage({
              id: 'DocumentEditingDetailCard.deleteConfigurationDescription2',
              defaultMessage:
                'Are you sure you want to remove the Microsoft Word integration for your entire organization?',
              description:
                'Second paragraph of description for delete configuration modal',
            })}
          </Text>
        </Layout>
      }
      onHide={handleHideDeleteModal}
    />
  );

  const getStatusText = () => {
    switch (integrationConfigurationStatus?.status) {
      case SharepointStatusType.Connected:
        return intl.formatMessage({
          id: 'DocumentEditingDetailCard.connectedText',
          defaultMessage: 'Connected',
          description: 'Connected status text',
        });
      case SharepointStatusType.AuthorizedConfigurationRequired:
        return intl.formatMessage({
          id: 'DocumentEditingDetailCard.authorizedConfigurationRequiredText',
          defaultMessage: 'Authorized - Configuration Required',
          description: 'Authorized - Configuration Required status text',
        });
      case SharepointStatusType.AuthorizedValidationError:
        return intl.formatMessage({
          id: 'DocumentEditingDetailCard.authorizedValidationErrorText',
          defaultMessage: 'Authorized - Validation Error',
          description: 'Authorized - Validation Error status text',
        });
      case SharepointStatusType.AuthenticationError:
        return intl.formatMessage({
          id: 'DocumentEditingDetailCard.authenticationErrorText',
          defaultMessage: 'Authentication Error',
          description: 'Authentication Error status text',
        });
      case SharepointStatusType.NotConnected:
      default:
        return intl.formatMessage({
          id: 'DocumentEditingDetailCard.notConnectedText',
          defaultMessage: 'Not Connected',
          description: 'Not Connected status text',
        });
    }
  };

  const getStatus = () => {
    switch (integrationConfigurationStatus?.status) {
      case SharepointStatusType.Connected:
        return 'success';
      case SharepointStatusType.NotConnected:
        return 'info';
      case SharepointStatusType.AuthorizedConfigurationRequired:
        return 'warning';
      case SharepointStatusType.AuthorizedValidationError:
      case SharepointStatusType.AuthenticationError:
        return 'danger';
      default:
        return 'info';
    }
  };

  const configureModalChildren = (
    <Layout direction="column" spacing={4} preset="sections">
      <Layout direction="column" spacing={2}>
        <Text variant="body-bold">
          {intl.formatMessage({
            id: 'DocumentEditingDetailCard.configureModalDescriptionTitle',
            defaultMessage: 'Integrate your environment',
            description: 'Title for configure modal',
          })}
        </Text>
        <Text preset="description">
          {intl.formatMessage({
            id: 'DocumentEditingDetailCard.configureModalDescription',
            defaultMessage:
              'Enter the SharePoint Site URL and Tenant ID to integrate with your environment.',
            description: 'Description for configure modal',
          })}
        </Text>
      </Layout>
      <FormField
        name="sharepointUrl"
        label={intl.formatMessage({
          id: 'DocumentEditingDetailCard.sharepointUrlLabel',
          defaultMessage: 'SharePoint Site URL',
          description: 'SharePoint Site URL label',
        })}
        placeholder={intl.formatMessage({
          id: 'DocumentEditingDetailCard.sharepointUrlPlaceholder',
          defaultMessage: 'e.g. https://...',
          description: 'SharePoint Site URL placeholder',
        })}
        value={integrationConfiguration?.sharepointUrl}
        input={TextInput}
        onChange={(value) => {
          setIntegrationConfiguration({
            ...integrationConfiguration,
            sharepointUrl: value as string,
          });
        }}
        info={intl.formatMessage({
          id: 'DocumentEditingDetailCard.sharepointUrlInfo',
          defaultMessage:
            'e.g. https://www.yourcompany.sharepoint.com/sites/yoursite',
          description: 'SharePoint Site URL info',
        })}
      />
      <FormField
        name="tenantId"
        label={intl.formatMessage({
          id: 'DocumentEditingDetailCard.tenantIdLabel',
          defaultMessage: 'Microsoft Tenant ID',
          description: 'Microsoft Tenant ID label',
        })}
        placeholder={intl.formatMessage({
          id: 'DocumentEditingDetailCard.tenantIdPlaceholder',
          defaultMessage: 'e.g. 1234567',
          description: 'Microsoft Tenant ID placeholder',
        })}
        value={integrationConfiguration?.tenantId}
        onChange={(value) => {
          setIntegrationConfiguration({
            ...integrationConfiguration,
            tenantId: value as string,
          });
        }}
        input={TextInput}
        info={intl.formatMessage({
          id: 'DocumentEditingDetailCard.tenantIdInfo',
          defaultMessage: 'Your Microsoft tenant ID (GUID format)',
          description: 'Microsoft Tenant ID info',
        })}
      />
    </Layout>
  );

  const configureModal = (
    <Modal
      enableHideOnPrimaryAction={true}
      isVisible={isConfigureModalVisible}
      primaryAction={{
        variant: 'primary',
        text: intl.formatMessage({
          id: 'DocumentEditingDetailCard.saveConfigurationButton',
          defaultMessage: 'Save Configuration',
          description: 'Save configuration button text in confirmation modal',
        }),
        onClick: () =>
          saveConfiguration(
            integrationConfiguration.tenantId,
            integrationConfiguration.sharepointUrl,
          ),
      }}
      cancelText={intl.formatMessage({
        id: 'DocumentEditingDetailCard.skipConfigurationButton',
        defaultMessage: 'Skip for Now',
        description: 'Skip for now button text in confirmation modal',
      })}
      title={intl.formatMessage({
        id: 'DocumentEditingDetailCard.configureModalTitle',
        defaultMessage: 'Microsoft Word is Authenticated',
        description: 'Title for configure modal',
      })}
      onHide={handleHideConfigureModal}
      children={configureModalChildren}
    />
  );

  const showConfigureButton = () => {
    if (isCollapsed === true) {
      return (
        integrationConfigurationStatus?.status ===
        SharepointStatusType.NotConnected
      );
    } else {
      return false;
    }
  };

  return (
    <IntegrationDetailCard
      isCollapsed={isCollapsed}
      footer={footer}
      headerRightContent={
        showConfigureButton() ? (
          <Button
            text={rightActionText}
            variant="action"
            onClick={handleConfigure}
          />
        ) : (
          <Layout align="center">
            <Chip
              mode="tag"
              text={getStatusText()}
              status={getStatus()}
              tooltip={getStatusInfo()}
            />
            {!isCollapsed && (
              <IconButton
                icon="trash"
                size="m"
                tooltip={intl.formatMessage({
                  id: 'DocumentEditingDetailCard.delete',
                  defaultMessage: 'Delete',
                  description:
                    'Delete button tooltip for Document Editing Integration Card',
                })}
                onClick={() => {
                  setIsDeleteConfigurationModalVisible(true);
                }}
                disabled={
                  isLoading ||
                  isDeleting ||
                  integrationConfigurationStatus?.status ===
                    SharepointStatusType.NotConnected
                }
              />
            )}
          </Layout>
        )
      }
      logoSrc={logoSrc}
      title={title}
      onToggleCollapse={setIsCollapsed}
    >
      <MicrosoftWordIntegrationCard
        integrationConfigurationStatus={integrationConfigurationStatus}
        saveConfiguration={saveConfiguration}
        handleGrantAdminConsent={() => handleGrantAdminConsent()}
        isLoading={isLoading || isConsentLoading || isDeleting}
      />
      {configureModal}
      {deleteModal}
    </IntegrationDetailCard>
  );
}

export default DocumentEditingDetailCard;
